import request from './axios';
import { DATE_RANGES_OPTION } from './dateTimeHelpers';

/**
 * @typedef {object} Result
 * @property {number} postingId
 * @property {number} categoryId
 * @property {string} headline
 * @property {string} DateModified - M/D/YYYY LTS
 * @property {?number} PublicationMasterCode
 * @property {string} PublicationMaster
 * @property {string} PublishDate - M/D/YYYY LTS
 * @property {string} State
 * @property {string} Subject
 * @property {string} MeetingStartDate - M/D/YYYY LTS
 * @property {string} MeetingEndDate - M/D/YYYY LTS
 * @property {?number} MeetingLocationId
 * @property {string} MeetingLocation
 * @property {string} summary
 * @property {?number} iconId
 * @property {string} Icon
 */

/**
 * Request for content page with ported query params from old urls.
 * Use single = true for one result, otherwise an array or results is returned.
 *
 * @param {object} options - see typedef
 * @param {number|string} options.pubCode -
 * @param {number|string} options.stateCode -
 * @param {number|string} options.statecode -
 * @param {number|string} options.yearsBack -
 * @param {number|string} options.meetingTypeId -
 * @param {string} options.sortField - viable values: [pagetitle, owner, and status]. Otherwise ignored.
 * @param {boolean} [options.single=true] -
 * @param {number|string} [cat1Id=null] - Publications -> 9996, Meetings -> 9997, News Releases -> 9998
 * @returns {Result|Array<Result>} - Result(s) -- See typedef
 */
export default async function fetchContent(options, axiosConfig = {}) {
  const fetchOptionsKeys = Object.keys(options);
  const onlyFetchOptions =
    fetchOptionsKeys.length === 1 && fetchOptionsKeys[0] === 'fieldOptionsToReturn';

  const { single = false, fieldOptionsToReturn = [] } = options;

  const body = generatePostData(options);

  try {
    /** @type {import("axios").AxiosResponse<import("@/src/types/swagger").ISearchDTO>} */
    let searchResponse = { data: { results: [], count: 0 } };
    if (!onlyFetchOptions) {
      searchResponse = await request({
        url: `cms/search`,
        method: 'POST',
        data: body,
        cache: true,
        cacheTime: 15,
        ...axiosConfig,
      });
    }

    const results = searchResponse.data.results || searchResponse.data.Results;
    const count = searchResponse.data.count || searchResponse.data.Count;

    const searchOptions = {};
    if (fieldOptionsToReturn.length) {
      const optionsResponse = await request('cms/searchoptions', {
        cache: true,
        cacheTime: 60 * 24,
        ...axiosConfig,
      });
      const { FieldOptions } = optionsResponse.data;
      for (let i = 0; i < fieldOptionsToReturn.length; i += 1) {
        const foKey = fieldOptionsToReturn[i];
        searchOptions[foKey] = FieldOptions[foKey];
      }
    }

    if (onlyFetchOptions) return { options: searchOptions };

    if (!results || (Array.isArray(results) && !results.length)) {
      throw new Error(
        `Page Data not found with options: ${Object.entries(options)
          .map(([key, val]) => `${key}: ${val}`)
          .join(' | ')}`
      );
    }

    return {
      results: single ? results[0] : results.filter(result => (result.postingId || result.SitePageId)),
      options: searchOptions,
      cached: searchResponse.cached,
      count,
    };
  } catch (error) {
    throw new Error(
      `Catch: Page Data not found with options: ${Object.entries(options)
        .map(([key, val]) => `${key}: ${val}`)
        .join(' | ')}`
    );
  }
}

export function generatePostData(options) {
  const {
    pubCode, // PublicationCode in Publications
    stateCode, // StateCode in States
    statecode, // StateCode in States coming from dynamic route segment prop
    yearsBack, // DayRangeCode (number of days to go back; e.g. for 5 years it would be 1825) in the DayRanges array (only one value will be expected)
    meetingTypeId, // MeetingVenueId (4 for conference, 5 for meeting, 6 for webinar) in MeetingVenues
    sortField, // pagetitle, owner, and status (otherwise it defaults to publish date)
    cat1Id = null,
    date = null,
    includeOnHomePage = null,
    dateStart = null,
    dateEnd = null,
    committees = null,
    sortOrder = null,
    pageSize = null,
    page = null,
  } = options;

  const _statecode = stateCode !== undefined ? stateCode : statecode;
  const _cat1Id = cat1Id !== undefined && cat1Id !== null ? cat1Id.toString().split(',') : cat1Id;

  return JSON.stringify({
    SearchId: null,
    PersonId: null,
    SearchDescription: null,
    FieldOptions: {
      SearchText: '',
      Page: page,
      PageSize: pageSize,
      SortOrder: sortOrder,
      LinesOfBusiness: [],
      Subjects: [],
      Publications: pubCode !== undefined ? [{ PublicationCode: pubCode }] : [],
      States: _statecode ? [{ StateCode: _statecode }] : [],
      DayRanges: yearsBack !== undefined ? [{ DayRangeCode: Number(yearsBack) * 365, DayRangeDescription: '' }] : [],
      MeetingTypes: meetingTypeId ? [{ MeetingTypeId: meetingTypeId }] : [],
      SortField:
        sortField && ['pagetitle', 'owner', 'status', 'state', 'upcomingevents'].includes(sortField)
          ? sortField
          : null,
      ...(_cat1Id && {
        PostingTypes: _cat1Id.map(Cat1Id => ({ Cat1Id })),
      }),
      IncludeOnHomePage: includeOnHomePage,
      ...(date && {
        DateStart: dateStart,
        DateEnd: dateEnd,
        DateRanges: DATE_RANGES_OPTION,
      }),
      ...(committees && {
        Committees: committees.map(({ CommitteeCode }) => ({ CommitteeCode })),
      }),
    },
  });
}
